import { beerActionTypes as actionTypes } from "./types";

const MAX_RETRIES = 10;
const RETRY_DELAY = 5000; // 5 seconds

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Circuit breaker flag to stop all beer-related network requests 
 * globally once a permanent failure occurs.
 */
let isApiHalted = false;

/**
 * Shared helper to handle fetch with retry logic.
 * This ensures that retries only happen for network/fetch failures.
 */
const fetchWithRetry = async (url, maxAttempts = MAX_RETRIES) => {
  if (isApiHalted) {
    throw new Error("API requests are halted due to previous permanent failure.");
  }

  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxAttempts) {
        console.error(`[FINAL ATTEMPT ${attempt}/${maxAttempts}] Permanently failed: ${url}. Error: ${error.message}`);
        isApiHalted = true; // Trip the circuit breaker
        break;
      }

      console.warn(`[Attempt ${attempt}/${maxAttempts}] Failed: ${url}. Retrying in ${RETRY_DELAY / 1000}s...`);
      await sleep(RETRY_DELAY);
    }
  }
  throw lastError;
};

export const fetchBeers = (page = 1) => async (dispatch, getState) => {
  if (page === 1) isApiHalted = false; // Reset circuit breaker on a fresh page 1 load

  const state = getState().beer || {};
  // Guard: Stop if already loading, if circuit is broken, or if we have an error
  if (state.isLoading || state.error || isApiHalted) return;

  dispatch({ type: actionTypes.FETCHING_BEERS, payload: { isLoading: true } });

  try {
    const url = `https://api.punkapi.com/v2/beers?page=${page}`;
    const beers = await fetchWithRetry(url);
    dispatch({
      type: actionTypes.FETCH_BEERS,
      payload: { beers, page, isLoading: false, error: null }
    });
  } catch (error) {
    console.error("fetchBeers failed permanently after max retries:", error);
    dispatch({
      type: actionTypes.FETCH_BEERS,
      payload: { beers: [], page, isLoading: false, error: "Failed to load beers after several attempts." }
    });
  }
};

export const fetchMoreBeers = page => async (dispatch, getState) => {
  const state = getState().beer || {};
  // Guard: Prevent Page 2+ from starting if the circuit is broken
  if (state.isLoading || state.error || isApiHalted) return;

  dispatch({ type: actionTypes.FETCHING_BEERS, payload: { isLoading: true } });

  try {
    const url = `https://api.punkapi.com/v2/beers?page=${page}`;
    const beers = await fetchWithRetry(url);
    dispatch({
      type: actionTypes.FETCH_MORE_BEERS,
      payload: { beers, page, isLoading: false, error: null }
    });
  } catch (error) {
    console.error("fetchMoreBeers failed permanently after max retries:", error);
    dispatch({
      type: actionTypes.FETCH_MORE_BEERS,
      payload: { beers: [], page, isLoading: false, error: "Failed to load more data." }
    });
  }
};

export const searchBeers = keyword => async (dispatch, getState) => {
  isApiHalted = false; // Reset circuit breaker when user performs a new search

  // Prevent multiple simultaneous searches
  if (getState().beer?.isLoading || isApiHalted) return;

  // dispatch loading state: true
  dispatch({ type: actionTypes.FETCHING_BEERS, payload: { isLoading: true } });

  try {
    const url = `https://api.punkapi.com/v2/beers?beer_name=${encodeURIComponent(keyword)}`;
    const beers = await fetchWithRetry(url);
    dispatch({
      type: actionTypes.SEARCH_BEERS,
      payload: { beers, isLoading: false, searchQuery: keyword }
    });
  } catch (error) {
    console.error("searchBeers failed permanently after max retries:", error);
    dispatch({ type: actionTypes.SEARCH_BEERS, payload: { beers: [], isLoading: false, searchQuery: keyword } });
  }
};

export const handleFavourite = beer => dispatch =>
  dispatch({
    type: actionTypes.HANDLE_FAVOURITE_BEER,
    payload: { beer }
  });

export const displayBeer = beer => async (dispatch, getState) => {
  // Prevent details fetch if circuit is broken
  if (getState().beer?.isLoading || isApiHalted) return;

  // dispatch loading state: true
  dispatch({ type: actionTypes.FETCHING_BEERS, payload: { isLoading: true } });

  try {
    // fetch similar beers based on their yeast ingredient
    const yeast = encodeURIComponent(beer.ingredients.yeast);
    const url = `https://api.punkapi.com/v2/beers?per_page=3&yeast=${yeast}`;
    const beers = await fetchWithRetry(url);
    
    // compose selected beer and similar beers into bundle
    let selected = { ...beer, similar: beers };
    dispatch({
      type: actionTypes.DISPLAY_BEER,
      payload: { selected, isLoading: false }
    });
  } catch (error) {
    console.error("displayBeer failed permanently after max retries:", error);
    dispatch({
      type: actionTypes.DISPLAY_BEER,
      payload: { selected: beer, isLoading: false }
    });
  }
};
